'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { ArrowRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface SignUpFormData {
  firstName: string;
  lastName: string;
  service: string;
}

const SERVICES = [
  'Classic Portrait Session',
  'Outdoor Lifestyle Session',
  'Cap & Gown Formal Session',
  'Full Senior Package (All Looks)',
];

interface Props {
  onSwitchMode: () => void;
}

export default function SignUpForm({ onSwitchMode }: Props) {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignUpFormData>();

  const onSubmit = async (_data: SignUpFormData) => {
    setIsLoading(true);
    await new Promise((r) => setTimeout(r, 1000));
    setIsLoading(false);
    toast.success('Got it! Let\'s book your session.');
    setTimeout(() => router.push('/booking-scheduling-screen'), 900);
  };

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Name row */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5">First Name</label>
            <input
              type="text"
              placeholder="Maya"
              className={`input-field bg-[#1a1a2e] border-[#2e2e4a] text-white placeholder-gray-500 focus:border-[#c9a96e] ${errors.firstName ? 'border-red-400' : ''}`}
              {...register('firstName', { required: 'Required' })}
            />
            {errors.firstName && <p className="mt-1 text-xs text-red-400">{errors.firstName.message}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5">Last Name</label>
            <input
              type="text"
              placeholder="Krishnamurthy"
              className={`input-field bg-[#1a1a2e] border-[#2e2e4a] text-white placeholder-gray-500 focus:border-[#c9a96e] ${errors.lastName ? 'border-red-400' : ''}`}
              {...register('lastName', { required: 'Required' })}
            />
            {errors.lastName && <p className="mt-1 text-xs text-red-400">{errors.lastName.message}</p>}
          </div>
        </div>

        {/* Service Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1.5">Service Needed</label>
          <select
            className={`input-field bg-[#1a1a2e] border-[#2e2e4a] text-white focus:border-[#c9a96e] ${errors.service ? 'border-red-400' : ''}`}
            {...register('service', { required: 'Please select a service' })}
          >
            <option value="" className="bg-[#1a1a2e]">Select a service…</option>
            {SERVICES.map((s) => (
              <option key={s} value={s} className="bg-[#1a1a2e]">{s}</option>
            ))}
          </select>
          {errors.service && <p className="mt-1 text-xs text-red-400">{errors.service.message}</p>}
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-[#1a1a2e] text-[#c9a96e] py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all duration-150 hover:bg-[#252540] active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed mt-2"
          style={{ minHeight: '48px' }}
        >
          {isLoading ? (
            <Loader2 size={18} className="animate-spin" />
          ) : (
            <>
              Continue to Booking
              <ArrowRight size={16} />
            </>
          )}
        </button>

        <p className="text-center text-sm text-gray-400">
          Already have an account?{' '}
          <button type="button" onClick={onSwitchMode} className="text-[#c9a96e] hover:underline font-medium">
            Sign in
          </button>
        </p>
      </form>
    </div>
  );
}